import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import GodConsumerModal from '../components/GodConsumerModal';

const GodConsumerPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [selected, setSelected] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showView, setShowView] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true'
    });

    const fetchItems = async (page = 1, search = '', consumerType = '') => {
        try {
            setLoading(true);
            let url = `${process.env.REACT_APP_API_URL}/api/consumers?page=${page}&limit=20&sort=-createdAt`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (consumerType) url += `&consumerType=${consumerType}`;
            const res = await axios.get(url, { headers: authHeaders() });
            if (res.data.success) {
                setItems(res.data.data);
                setTotal(res.data.pagination.total);
                setTotalPages(res.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch consumers: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems(currentPage, searchTerm, typeFilter);
    }, [currentPage, searchTerm, typeFilter]);

    useEffect(() => {
        if (searchParams.get('open') === 'create') {
            setShowCreate(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleDelete = async () => {
        if (!selected) return;
        try {
            setDeleteLoading(true);
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/consumers/${selected._id}`,
                { headers: authHeaders() }
            );
            setShowDelete(false);
            setSelected(null);
            fetchItems(currentPage, searchTerm, typeFilter);
        } catch (err) {
            setError(`Failed to delete: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const openEdit = async (row) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/consumers/${row._id}`,
                { headers: authHeaders() }
            );
            if (res.data.success) {
                setSelected(res.data.data);
                setShowEdit(true);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    const openView = async (row) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/consumers/${row._id}`,
                { headers: authHeaders() }
            );
            if (res.data.success) {
                setSelected(res.data.data);
                setShowView(true);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Consumers</h1>
                        <p className="text-gray-600">Total: {total}</p>
                    </div>
                    <button
                        onClick={() => setShowCreate(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create Consumer
                    </button>
                </div>

                <div className="mb-6 flex flex-wrap gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                        <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-blue-700 font-bold"
                            placeholder="Search username, email, or display name..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (setSearchTerm(searchInput), setCurrentPage(1))}
                        />
                        <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    <select
                        value={typeFilter}
                        onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-blue-700 font-bold"
                    >
                        <option value="">All types</option>
                        <option value="consumer">consumer</option>
                        <option value="mama">mama</option>
                    </select>
                    <button
                        onClick={() => { setSearchTerm(searchInput); setCurrentPage(1); }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Search
                    </button>
                    <button
                        onClick={() => { setSearchInput(''); setSearchTerm(''); setTypeFilter(''); setCurrentPage(1); }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                        Clear
                    </button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                {showCreate && (
                    <GodConsumerModal
                        mode="create"
                        onSuccess={() => { setShowCreate(false); fetchItems(currentPage, searchTerm, typeFilter); }}
                        onCancel={() => setShowCreate(false)}
                    />
                )}
                {showEdit && selected && (
                    <GodConsumerModal
                        mode="edit"
                        consumer={selected}
                        onSuccess={() => { setShowEdit(false); setSelected(null); fetchItems(currentPage, searchTerm, typeFilter); }}
                        onCancel={() => { setShowEdit(false); setSelected(null); }}
                    />
                )}
                {showDelete && selected && (
                    <DeleteConfirmationModal
                        title="Delete Consumer"
                        message="Delete this consumer?"
                        itemName={selected.displayName || selected.username || selected.useremail || selected._id}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => { setShowDelete(false); setSelected(null); }}
                    />
                )}
                {showView && selected && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-start justify-center pt-16 p-4 overflow-y-auto">
                        <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6">
                            <div className="flex justify-between mb-4">
                                <h3 className="text-lg font-semibold">Consumer Details</h3>
                                <button onClick={() => { setShowView(false); setSelected(null); }} className="text-gray-500">
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="text-sm space-y-2 text-blue-700">
                                <p><span className="text-gray-600">Username:</span> {selected.username || '—'}</p>
                                <p><span className="text-gray-600">Display name:</span> {selected.displayName || '—'}</p>
                                <p><span className="text-gray-600">Email:</span> {selected.useremail || '—'}</p>
                                <p><span className="text-gray-600">Type:</span> {selected.consumerType || '—'}</p>
                                <p><span className="text-gray-600">Old import:</span> {selected.oldSiteImport ? 'Yes' : 'No'}</p>
                                <p><span className="text-gray-600">Reset pwd flag:</span> {selected.resetPasswordForOldImport ? 'Yes' : 'No'}</p>
                                <p><span className="text-gray-600">Favourites:</span> {selected.favourite?.length ?? 0}</p>
                                {selected.favourite?.length > 0 && (
                                    <ul className="list-disc pl-5 text-xs">
                                        {selected.favourite.map((f, i) => (
                                            <li key={i}>[{f.favouriteForModel}] {String(f.favouriteFor)}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Display name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Favourites</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={6} className="px-4 py-6 text-center">Loading...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-6 text-center">No consumers found</td></tr>
                            ) : (
                                items.map((row) => (
                                    <tr key={row._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm">{row.username || '—'}</td>
                                        <td className="px-4 py-3 text-sm">{row.displayName || '—'}</td>
                                        <td className="px-4 py-3 text-sm">{row.useremail || '—'}</td>
                                        <td className="px-4 py-3 text-sm">{row.consumerType || '—'}</td>
                                        <td className="px-4 py-3 text-sm">{row.favourite?.length ?? 0}</td>
                                        <td className="px-4 py-3 text-right space-x-1">
                                            <button onClick={() => openView(row)} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-full" title="View"><FiEye /></button>
                                            <button onClick={() => openEdit(row)} className="p-1.5 text-green-600 hover:bg-green-100 rounded-full" title="Edit"><FiEdit2 /></button>
                                            <button onClick={() => { setSelected(row); setShowDelete(true); }} className="p-1.5 text-red-600 hover:bg-red-100 rounded-full" title="Delete"><FiTrash2 /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
        </Layout>
    );
};

export default GodConsumerPage;
